import { useState, type ReactNode } from 'react'
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError, type DraftStatus, type SectionTopic } from '../api/client'
import { addExclude, addInclude, defaultsDestination, describeBrief, isWebUrl, supportsMembership, withDestinationFor, type Brief, type SourceRef } from '../lib/brief'
import { describeError } from '../lib/describeError'
import { hitRef, hitTitle, refKey, refTitle, type ArchiveHit } from '../lib/reviewSources'
import { pieceTypeKeys, pieceTypeLabel } from '../lib/sections'
import { ArchiveSearch } from '../pages/ReviewSources'
import { draftNow, getIdeas, getMyDefaults, getPieceForRedraft, putMyDefaults, type DeskResponse } from './api'
import { withRunStarted } from './desk'
import { ASK_MAX, IDEA_FAILED, IDEA_GONE, NOTES_MAX, SUBJECT_MAX, cancelTarget, estimateMinutes, ideaLoad, initialStart, primaryLabel, runningBlock, startBody, startRefusal, submitBlock, withStartFormat, type StartState } from './start'
import { deskHref, importsHref } from './links'
import { useImportsOn } from './useImportsOn'
import { useCanDraft, useEditorialTitle } from './titleContext'

const focus = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-glow'
const step = 'flex flex-col gap-3.5 rounded-xl border border-ink-500 bg-ink-700 p-5'
const field = `min-h-11 w-full rounded-lg border border-ink-400 bg-ink-800 px-3 py-2.5 font-ui text-[14px] text-paper placeholder:text-paper-faint ${focus}`
const btn = `inline-flex min-h-9 items-center justify-center whitespace-nowrap rounded-lg border border-ink-400 px-3 font-ui text-[13px] font-semibold text-paper hover:bg-ink-600 disabled:opacity-50 ${focus}`

function Step({ n, title, note, children }: { n: number; title: string; note?: string; children: ReactNode }) {
  return (
    <section className={step}>
      <h2 className="m-0 flex items-center gap-2.5 font-ui text-[17px] font-semibold text-paper">
        <span className="inline-flex h-[26px] w-[26px] flex-none items-center justify-center rounded-full bg-paper font-ui text-[13px] font-bold text-ink-900" aria-hidden="true">{n}</span>
        {title}
        {note && <span className="text-[13px] font-normal text-paper-dim">{note}</span>}
      </h2>
      {children}
    </section>
  )
}

type Card = { key: string; label: string; hint: string; extra?: string; preview?: ReactNode }

function Cards({ name, legend, value, options, onPick, min = 190 }: { name: string; legend: string; value: string; options: Card[]; onPick: (k: string) => void; min?: number }) {
  return (
    <fieldset className="m-0 grid gap-2.5 border-0 p-0" style={{ gridTemplateColumns: `repeat(auto-fit, minmax(min(${min}px, 100%), 1fr))` }}>
      <legend className="sr-only">{legend}</legend>
      {options.map((o) => (
        <label key={o.key} className={`relative flex cursor-pointer flex-col gap-1.5 rounded-[10px] border p-4 ${value === o.key ? 'border-amber-glow bg-amber-wash ring-1 ring-amber-glow' : 'border-ink-400 bg-ink-800'}`}>
          <input type="radio" name={name} checked={value === o.key} onChange={() => onPick(o.key)} className="absolute right-4 top-4 m-0 h-[18px] w-[18px] accent-amber-glow" />
          <strong className="pr-6 font-ui text-[16px] font-semibold text-paper">{o.label}</strong>
          <span className="font-ui text-[13px] text-paper-dim">{o.hint}</span>
          {o.extra && <span className="font-ui text-[12px] text-paper-faint">{o.extra}</span>}
          {o.preview}
        </label>
      ))}
    </fieldset>
  )
}

/** draft-now said no with a 202: another piece is being written for this title. */
class Refused extends Error {}

const Bar = ({ w = 'w-full', strong = false }: { w?: string; strong?: boolean }) =>
  <span className={`block rounded-sm ${strong ? 'h-1.5 bg-amber-deep' : 'h-1 bg-ink-400'} ${w}`} />
const Mini = () => <span className="flex flex-1 flex-col gap-1 rounded-md bg-ink-700 p-2.5"><Bar w="w-[70%]" strong /><Bar /><Bar /><Bar w="w-[80%]" /></span>
const Preview = ({ children }: { children: ReactNode }) => <span aria-hidden="true" className="mt-1.5 flex gap-1.5">{children}</span>

const FORMAT_CARDS: Card[] = [
  { key: 'short', label: 'Short', hint: 'About 250 words', extra: 'Ready in about 3 minutes' },
  { key: 'long', label: 'Long', hint: 'About 1,000 words', extra: 'Ready in about 6 minutes' },
  { key: 'deep', label: 'Deep research', hint: '2,000 words or more, researched question by question', extra: 'Ready in 10 to 20 minutes' },
]
const SOURCE_CARDS: Card[] = [
  { key: 'internal', label: 'Our archive', hint: 'Member articles, the free site and the magazine issues. No web.' },
  { key: 'external', label: 'The web', hint: 'Researched on the web. Still links into the member archive.' },
  { key: 'both', label: 'Both', hint: 'Our archive first, the web to fill gaps. Marked when the web was used.' },
]

function StartForm({ initial, from, defaultsDest, typed }: { initial: StartState; from: string | null; defaultsDest: Brief['destination'] | undefined; typed: boolean }) {
  const { section, publicationSlug: slug } = useEditorialTitle()
  const nav = useNavigate()
  const location = useLocation()
  const qc = useQueryClient()
  const desk = deskHref(section.path)
  const importsOn = useImportsOn(slug)
  const [s, setS] = useState<StartState>(initial)
  const b = s.brief
  const setB = (brief: Brief) => setS((p) => ({ ...p, brief }))
  const [titles, setTitles] = useState<Record<string, string>>({})
  const [link, setLink] = useState('')
  const [pinError, setPinError] = useState<string | null>(null)
  const [webDropped, setWebDropped] = useState(false)
  const [saveDefaults, setSaveDefaults] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const membership = supportsMembership(slug)
  const webAllowed = b.sources !== 'internal'

  const pin = (ref: SourceRef, title?: string) => {
    const out = addInclude(b, ref)
    setPinError(out.error ?? null)
    if (out.error) return false
    if (title) setTitles((t) => ({ ...t, [refKey(ref)]: title }))
    setB(out.brief)
    return true
  }
  const avoid = (h: ArchiveHit) => {
    setPinError(b.exclude.length >= 30 ? 'At most 30 blocked sources.' : null)
    setTitles((t) => ({ ...t, [refKey(hitRef(h))]: hitTitle(h) }))
    setB(addExclude(b, hitRef(h)))
  }
  const unlist = (which: 'include' | 'exclude', ref: SourceRef) => setB({ ...b, [which]: b[which].filter((r) => refKey(r) !== refKey(ref)) })
  const addLink = () => {
    const url = link.trim()
    if (!isWebUrl(url)) { setPinError('Paste a full http:// or https:// link (500 characters at most).'); return }
    if (pin({ kind: 'web', url })) setLink('')
  }
  const pickSources = (sources: Brief['sources']) => {
    const dropped = sources === 'internal' && b.include.some((r) => r.kind === 'web')
    setWebDropped(dropped)
    setB(dropped ? { ...b, sources, include: b.include.filter((r) => r.kind !== 'web') } : { ...b, sources })
  }

  // While the title's writer is busy, a Start would only be refused: say so and wait.
  const status = useQuery({
    queryKey: ['section-draft-status', slug],
    queryFn: () => api.get<DraftStatus>(`/api/lab/section-drafts/draft-now/status?publication_slug=${slug}`),
    refetchInterval: 10_000,
  })

  const start = useMutation({
    mutationFn: async () => {
      const body = startBody(s, typed)
      body.brief = withDestinationFor(body.brief, slug)
      const res = await draftNow(slug, body)
      // A 202 can still be a refusal (another piece is being written): nothing started, so nothing is saved.
      const refused = startRefusal(res)
      if (refused) throw new Refused(refused)
      // the Desk shows the run the moment it opens, before its first check
      qc.setQueryData<DeskResponse>(['editorial-desk', slug], (d) => withRunStarted(d, res.status))
      // The save is a courtesy: the piece is already started, so a failure here must not look like one.
      if (saveDefaults) {
        try {
          await putMyDefaults(slug, { format: body.brief.format, sources: body.brief.sources, destination: defaultsDestination(body.brief, slug, defaultsDest) })
          qc.invalidateQueries({ queryKey: ['editorial-my-defaults', slug] })
        } catch { /* ignored */ }
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['editorial-desk', slug] })
      qc.invalidateQueries({ queryKey: ['section-draft-status', slug] })
      nav(desk)
    },
    onError: (e) => {
      if (e instanceof Refused) {
        setError(e.message)
        qc.invalidateQueries({ queryKey: ['section-draft-status', slug] })
        return
      }
      setError(e instanceof ApiError && e.status === 409 ? describeError(e) || 'A draft is already being written.' : describeError(e))
    },
  })

  const block = submitBlock(s) ?? runningBlock(status.data)
  const submit = () => { if (!block && !start.isPending) { setError(null); start.mutate() } }
  const cancel = () => {
    const to = cancelTarget(location.key, desk)
    if (to === -1) nav(-1)
    else nav(to)
  }
  const minutes = estimateMinutes(b.format === 'legacy' ? 'short' : b.format)
  const chosen = b.include.length + b.exclude.length
  const sourceWords: Record<Brief['sources'], string> = { internal: 'Our archive', external: 'The web', both: 'Our archive, then the web', internal_first: 'Our archive, then the web' }
  const readerWords = { section: `Everyone, on ${section.label}`, membership: 'Members only', both: `Members get it all; ${section.label} gets the summary` }
  const lengthWords = { short: 'Short, about 250 words', long: 'Long, about 1,000 words', deep: 'Deep research, 2,000 words or more', legacy: 'Short, about 250 words' }

  return (
    <div className="flex flex-col gap-5">
      <nav aria-label="Breadcrumb" className="font-ui text-[13px] text-paper-dim"><Link to={desk} className="text-amber-glow underline underline-offset-2">Desk</Link> / Start a piece</nav>
      <h1 className="m-0 font-serif text-[34px] font-medium leading-tight text-paper sm:text-[40px]">Start a piece</h1>
      {from && <p className="m-0 font-ui text-[14px] text-paper-dim">{from}</p>}
      {importsOn && <p className="m-0 font-ui text-[14px] text-paper-dim">Have it written already? <Link to={importsHref(section.path)} className="text-amber-glow underline underline-offset-2">Import a file</Link>.</p>}
      <div className="flex flex-wrap items-start gap-6">
        <form className="flex min-w-0 flex-[3_1_560px] flex-col gap-3.5" onSubmit={(e) => { e.preventDefault(); submit() }}>
          <Step n={1} title="What is it about?">
            <label className="flex flex-col gap-1.5 font-ui text-[14px] font-semibold text-paper">Subject
              <input className={field} type="text" maxLength={SUBJECT_MAX} value={s.subject} onChange={(e) => setS({ ...s, subject: e.target.value })} placeholder="e.g. Grumman F7F Tigercat" />
            </label>
            <label className="flex flex-col gap-1.5 font-ui text-[14px] font-semibold text-paper">
              <span>What should it answer? <span className="block text-[12px] font-normal text-paper-dim">Optional. The question a reader typed into Google is a good start.</span></span>
              <input className={field} type="text" maxLength={ASK_MAX} value={s.ask} onChange={(e) => setS({ ...s, ask: e.target.value })} placeholder="e.g. why it arrived too late for the war" />
            </label>
            {typed && (
              <label className="flex flex-col gap-1.5 font-ui text-[14px] font-semibold text-paper">Type
                <select className={field} value={s.pieceType} onChange={(e) => setS({ ...s, pieceType: e.target.value })}>
                  <option value="">Any</option>
                  {pieceTypeKeys(slug).map((k) => <option key={k} value={k}>{pieceTypeLabel(k)}</option>)}
                </select>
              </label>
            )}
          </Step>

          <Step n={2} title="How long?">
            <Cards name="len" legend="Length" value={b.format === 'legacy' ? 'short' : b.format} options={FORMAT_CARDS} onPick={(k) => setS(withStartFormat(s, k as Brief['format']))} />
            <p className="m-0 font-ui text-[12px] text-paper-dim">Lengths are a target, not a rule. The writer goes longer or shorter when the subject needs it, and you can ask for a shorter version later.</p>
          </Step>

          <Step n={3} title="Where should it come from?">
            <Cards name="src" legend="Sources" value={b.sources === 'internal_first' ? 'both' : b.sources} options={SOURCE_CARDS} onPick={(k) => pickSources(k as Brief['sources'])} />
            <p className="m-0 font-ui text-[12px] text-paper-dim">Whatever you choose, every piece links to at least one member article or magazine issue.</p>
          </Step>

          {membership && (
            <Step n={4} title="Who will read it?">
              <Cards name="dest" legend="Where it appears" value={b.destination} min={220} onPick={(k) => setB({ ...b, destination: k as Brief['destination'] })}
                options={[
                  { key: 'section', label: `Everyone, on ${section.label}`, hint: `Free on ${section.label}.`, preview: <Preview><Mini /></Preview> },
                  { key: 'membership', label: 'Members only', hint: 'In the member archive under Member exclusives.', preview: <Preview><Mini /></Preview> },
                  { key: 'both', label: 'Both', hint: `Members get the full piece. ${section.label} shows a short summary with “Read the full article →”.`, preview: <Preview><Mini /><Mini /></Preview> },
                ]} />
            </Step>
          )}

          <Step n={membership ? 5 : 4} title="Anything the writer should know?" note="Optional">
            <label className="flex flex-col gap-1.5">
              <span className="sr-only">Notes for the writer</span>
              <textarea className={field} rows={3} maxLength={NOTES_MAX} value={b.guidance} onChange={(e) => setB({ ...b, guidance: e.target.value })} placeholder="e.g. focus on the carrier trials; aim it at first-time builders" />
            </label>
            <details open className="flex flex-col gap-2.5">
              <summary className="cursor-pointer font-ui text-[14px] font-semibold text-paper">Sources to use or avoid {chosen > 0 && <span className="font-normal text-paper-dim">({chosen} chosen)</span>}</summary>
              <div className="mt-3 flex flex-col gap-2.5">
                <ArchiveSearch publicationSlug={slug} onUse={(h) => pin(hitRef(h), hitTitle(h))} onBlock={avoid} blockLabel="Avoid" />
                <label className="flex flex-col gap-1.5 font-ui text-[14px] font-semibold text-paper">Or paste a web page the writer should read
                  <span className="flex gap-2">
                    <input className={field} type="url" value={link} disabled={!webAllowed} maxLength={500} placeholder="https://"
                           onChange={(e) => setLink(e.target.value)}
                           onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (webAllowed) addLink() } }} />
                    <button type="button" className={btn} disabled={!webAllowed || !link.trim()} onClick={addLink}>Add</button>
                  </span>
                </label>
                {!webAllowed && <p className="m-0 font-ui text-[12px] text-paper-dim">Web links need The web or Both</p>}
                {webDropped && <p className="m-0 font-ui text-[12px] text-amber-glow">Web links removed. They need The web or Both</p>}
                {pinError && <p role="alert" className="m-0 font-ui text-[12px] text-radar-red">{pinError}</p>}
                {chosen > 0 && (
                  <ul className="m-0 flex list-none flex-wrap gap-2 p-0">
                    {b.include.map((r) => (
                      <li key={`in-${refKey(r)}`} className="inline-flex max-w-full items-center gap-1 rounded-full border border-radar-green px-2.5 py-1 font-ui text-[12px] font-semibold text-paper">
                        <span className="truncate">Use: {refTitle(r, titles)}</span>
                        <button type="button" aria-label={`Stop using ${refTitle(r, titles)}`} className={`ml-1 ${focus}`} onClick={() => unlist('include', r)}>×</button>
                      </li>
                    ))}
                    {b.exclude.map((r) => (
                      <li key={`ex-${refKey(r)}`} className="inline-flex max-w-full items-center gap-1 rounded-full border border-radar-red px-2.5 py-1 font-ui text-[12px] font-semibold text-paper">
                        <span className="truncate">Avoid: {refTitle(r, titles)}</span>
                        <button type="button" aria-label={`Allow ${refTitle(r, titles)} again`} className={`ml-1 ${focus}`} onClick={() => unlist('exclude', r)}>×</button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </details>
          </Step>
        </form>

        <aside aria-label="Your brief" className="sticky top-6 flex min-w-0 flex-[1_1_280px] flex-col gap-3.5 rounded-[14px] bg-paper p-5 text-ink-900">
          <span className="font-ui text-[12px] font-bold uppercase tracking-[0.08em] text-ink-500">Your brief</span>
          <span className="break-words font-serif text-[24px] leading-tight">{s.subject.trim() || 'Nothing yet'}</span>
          <dl className="m-0 grid gap-x-3.5 gap-y-2 font-ui text-[13px]" style={{ gridTemplateColumns: 'auto 1fr' }}>
            <dt className="text-ink-500">Length</dt><dd className="m-0">{lengthWords[b.format]}</dd>
            <dt className="text-ink-500">Sources</dt><dd className="m-0">{sourceWords[b.sources]}</dd>
            {membership && <><dt className="text-ink-500">Readers</dt><dd className="m-0">{readerWords[b.destination]}</dd></>}
            <dt className="text-ink-500">Notes</dt><dd className="m-0">{b.guidance.trim() ? 'Yes' : 'None'}</dd>
            <dt className="text-ink-500">Chosen</dt><dd className="m-0">{chosen ? `${b.include.length} to use, ${b.exclude.length} to avoid` : 'None'}</dd>
          </dl>
          <p className="m-0 font-ui text-[12px] text-ink-500">{describeBrief(b, section.label)}</p>
          <label className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-ink-900/20 p-3 font-ui text-[13px]">
            <input type="checkbox" className="mt-px h-[18px] w-[18px] accent-amber-deep" checked={b.review_sources}
                   onChange={(e) => setS({ ...s, touchedReview: true, brief: { ...b, review_sources: e.target.checked } })} />
            <span><strong className="font-semibold">Let me check the sources first</strong><br /><span className="text-ink-500">It stops after gathering so you can add or remove some. On by default for Deep research.</span></span>
          </label>
          <button type="button" disabled={!!block || start.isPending} title={block ?? undefined}
                  onClick={submit}
                  className={`inline-flex min-h-12 items-center justify-center rounded-lg border border-amber-deep bg-amber-deep px-4 font-ui text-[15px] font-semibold text-paper hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 ${focus}`}>
            {start.isPending ? 'Starting…' : primaryLabel(s)}
          </button>
          {block && <span className="text-center font-ui text-[12px] text-ink-500">{block}</span>}
          {error && <p role="alert" className="m-0 font-ui text-[13px] font-semibold text-amber-deep">{error}</p>}
          <span className="text-center font-ui text-[12px] text-ink-500">About {minutes} minutes. You can leave this page.</span>
          <div className="flex flex-wrap justify-between gap-2 border-t border-ink-900/20 pt-2.5 font-ui text-[13px]">
            <label className="flex cursor-pointer items-center gap-2"><input type="checkbox" className="h-4 w-4 accent-amber-deep" checked={saveDefaults} onChange={(e) => setSaveDefaults(e.target.checked)} /> Make these my usual settings</label>
            <button type="button" className={`underline ${focus}`} onClick={cancel}>Cancel</button>
          </div>
        </aside>
      </div>
    </div>
  )
}

export function StartPiecePage() {
  const { publicationSlug: slug } = useEditorialTitle()
  const canDraft = useCanDraft()
  const { path } = useParams()
  const [params] = useSearchParams()
  const fromId = Number(params.get('from')) || null
  const ideaLabel = fromId === null ? params.get('idea') : null
  const defaults = useQuery({ queryKey: ['editorial-my-defaults', slug], queryFn: () => getMyDefaults(slug), retry: false })
  const piece = useQuery({ queryKey: ['editorial-redraft', fromId], queryFn: () => getPieceForRedraft(fromId as number), enabled: fromId !== null, retry: false })
  const topics = useQuery({ queryKey: ['section-topics', slug], queryFn: () => api.get<{ items: SectionTopic[]; computed_at: string | null }>(`/api/lab/section-drafts/topics?publication_slug=${slug}`) })
  // Same query as the Ideas page, so an idea opened from there is already loaded.
  const ideas = useQuery({ queryKey: ['editorial', 'ideas', slug, 'all'], queryFn: () => getIdeas(slug, 'all'), enabled: ideaLabel !== null, retry: false })
  const loading = defaults.isLoading || (fromId !== null && piece.isLoading) || (ideaLabel !== null && ideas.isLoading)
  // The title-wide notice says why; there is no brief to fill in.
  if (!canDraft) return null
  if (loading) return <p className="font-ui text-[13px] text-paper-dim">Loading…</p>
  const from = piece.data ? `Starting again from “${piece.data.title ?? piece.data.topic?.label ?? 'an earlier piece'}”` : null
  const idea = ideaLabel !== null ? (ideas.data?.items ?? []).find((i) => i.label === ideaLabel) ?? null : null
  const ideaState = ideaLoad({ asked: ideaLabel !== null, isError: ideas.isError, error: ideas.error, found: idea !== null })
  const initial = initialStart(params, defaults.data, piece.data, idea?.topic)
  // Show Type when the title's subjects carry one, or when the subject brought its own.
  const typed = (topics.data?.items ?? []).some((t) => t.piece_type) || !!initial.base?.topic.piece_type
  return (
    <>
      {fromId !== null && piece.isError && <p role="alert" className="mb-4 font-ui text-[13px] text-amber-glow">Could not load that piece: {describeError(piece.error)}. Starting fresh.</p>}
      {ideaState === 'gone' && <p role="status" className="mb-4 font-ui text-[13px] text-amber-glow">{IDEA_GONE}</p>}
      {ideaState === 'failed' && (
        <p role="alert" className="m-0 flex flex-wrap items-center gap-3 mb-4 font-ui text-[13px] text-amber-glow">
          {IDEA_FAILED}
          <button type="button" className={btn} disabled={ideas.isFetching} onClick={() => void ideas.refetch()}>
            {ideas.isFetching ? 'Trying…' : 'Retry'}
          </button>
        </p>
      )}
      {/* Keyed on whether the idea loaded, so a Retry that finds it fills the whole subject. */}
      <StartForm key={`${path}-${fromId}-${ideaLabel}-${ideaState}`} initial={initial} from={from} defaultsDest={defaults.data?.destination} typed={typed} />
    </>
  )
}
